import {
    WorkyPasswordCriterionId,
    type WorkyPasswordCriterionEvaluation,
} from './WorkyPasswordCriteriaInterfaces';

const WORKY_PASSWORD_MINIMUM_LENGTH: number = 8;

const hasLowercaseLetter = (password: string): boolean => /[a-z]/.test(password);
const hasUppercaseLetter = (password: string): boolean => /[A-Z]/.test(password);
const hasNumber = (password: string): boolean => /\d/.test(password);
const hasMinimumLength = (password: string): boolean => password.length >= WORKY_PASSWORD_MINIMUM_LENGTH;
const hasSpecialCharacterOrSpace = (password: string): boolean => /[^A-Za-z0-9]/.test(password);
const hasNoLeadingOrTrailingSpace = (password: string): boolean => {
    if (password.length === 0) {
        return true;
    }
    return !password.startsWith(' ') && !password.endsWith(' ');
};

const evaluateWorkyPasswordCriteria = (password: string): readonly WorkyPasswordCriterionEvaluation[] => {
    return [
        {
            id: WorkyPasswordCriterionId.LOWERCASE,
            label: 'Password must contain a lower case letter',
            isMet: hasLowercaseLetter(password),
        },
        {
            id: WorkyPasswordCriterionId.UPPERCASE,
            label: 'Password must contain an upper case letter',
            isMet: hasUppercaseLetter(password),
        },
        {
            id: WorkyPasswordCriterionId.NUMBER,
            label: 'Password must contain a number',
            isMet: hasNumber(password),
        },
        {
            id: WorkyPasswordCriterionId.MINIMUM_LENGTH,
            label: 'Password must contain at least 8 characters',
            isMet: hasMinimumLength(password),
        },
        {
            id: WorkyPasswordCriterionId.SPECIAL_CHARACTER_OR_SPACE,
            label: 'Password must contain a special character or a space',
            isMet: hasSpecialCharacterOrSpace(password),
        },
        {
            id: WorkyPasswordCriterionId.NO_LEADING_OR_TRAILING_SPACE,
            label: 'Password must not contain a leading or trailing space',
            isMet: hasNoLeadingOrTrailingSpace(password),
        },
    ];
};

const isValidWorkyPassword = (password: string): boolean => {
    return evaluateWorkyPasswordCriteria(password).every(
        (criterionEvaluation: WorkyPasswordCriterionEvaluation): boolean => criterionEvaluation.isMet
    );
};

export { evaluateWorkyPasswordCriteria, isValidWorkyPassword };
